import { PermissionRole } from '@next-space/fe-api-idl';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { cache } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import type { DropInfoState } from 'src/services/app/hook/use-drop-info';
import { isPageLike } from 'src/utils/block-type-utils';
import { getDiffPermissions } from 'src/utils/permission-utils';
import { TocType } from 'src/views/main/aside/toc/types';
import { Aside } from './types';

export const permissionForbidden = (dropInfo: DropInfoState, dragIds: string[]) => {
  const { position, dropId, dropInChild, tocInfo, cursorArea } = dropInfo || {};

  if (!position || !dropId) return;

  const { blocks } = cache;
  const dropBlock = blocks[dropId];
  if (!dropBlock) return;
  const dropParentBlock = blocks[dropBlock.parentId];
  if (!dropParentBlock) return;

  if (tocInfo) {
    const { dragTocDeep, dropTocDeep, dragTocType, dropTocType } = tocInfo;

    // 同一个目录区域排序不做权限判断
    if (
      dragTocType === dropTocType &&
      dragTocDeep === 0 &&
      dropTocDeep === 0 &&
      (position === 'top' || position === 'bottom')
    ) {
      return false;
    }

    // 拖拽到 个人根目录 必须有全部权限
    if (
      dropTocType === TocType.PRIVATE &&
      dropTocDeep === 0 &&
      (position === 'top' || position === 'bottom')
    ) {
      if (dragIds.some((id) => getPermissions(id).role !== PermissionRole.EDITOR)) {
        return true;
      }
    }

    // 拖拽到 空间目录 如果已有空间权限 则不需要全部权限也可
    if (
      dropTocType === TocType.SPACE &&
      dropTocDeep === 0 &&
      (position === 'top' || position === 'bottom')
    ) {
      if (
        dragIds.some((id) => {
          const { permissions, role } = getPermissions(id);
          const { spacePermission } = getDiffPermissions(permissions);
          if (!spacePermission || spacePermission.role === PermissionRole.NONE) {
            return role !== PermissionRole.EDITOR;
          }
          return role === PermissionRole.NONE || role === PermissionRole.WRITER;
        })
      ) {
        return true;
      }
    }
  }

  const getParentPageId = (id?: string) => {
    if (!id) return;
    let curr: NextBlock | undefined = blocks[id];
    while (curr && !isPageLike(curr.type)) {
      curr = blocks[curr.parentId];
    }
    return curr?.uuid;
  };

  const forbidden = (parentId: string) => {
    if (
      // 拖拽的块需要满足以下条件
      dragIds.every((id) => {
        const { isRestricted, role: dragRole } = getPermissions(id);
        return (
          dragRole === PermissionRole.EDITOR ||
          (dragRole === PermissionRole.WRITER &&
            // 受限编辑可以随意拖动
            (isRestricted ||
              // 在同一个 page 级页面下
              getParentPageId(blocks[id]?.parentId) === getParentPageId(parentId)))
        );
      })
    ) {
      if (parentId === dropBlock.spaceId) return false;
      const { role } = getPermissions(parentId);
      return role === PermissionRole.READER || role === PermissionRole.NONE;
    }
    return true;
  };

  if (position === 'top') {
    return forbidden(dropParentBlock.uuid);
  } else if (position === 'middle') {
    return forbidden(dropBlock.uuid);
  } else if (position === 'bottom') {
    if (dropInChild) {
      return forbidden(dropBlock.uuid);
    } else if (
      cursorArea === Aside.BOTTOM &&
      tocInfo?.dragToc === dropId &&
      dropBlock.parentId !== dropBlock.spaceId
    ) {
      return forbidden(dropParentBlock.parentId);
    }
    return forbidden(dropParentBlock.uuid);
  }

  // 分栏 CASE
  return forbidden(dropBlock.uuid);
};
